import { motion } from "framer-motion";
import { Copy, Check, Landmark, MapPin, FileText, Layers, User, Download, Bookmark, Share2, Sparkles, ShieldCheck, Lightbulb, Lock, Loader2, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { startCheckout, useEntitlement } from "@/components/Payments";
import { downloadReportPdf } from "@/lib/reportPdf";
import { ProfessionalAssistance } from "@/components/ProAssistance";
import { SourceBadge } from "@/components/SourceBadge";
import { ParcelMapPreview } from "@/components/ParcelMapPreview";
import { DataSourceStatus } from "@/components/DataSourceStatus";
import { ProvenanceInspector } from "@/components/ProvenanceInspector";
import { SummaryBar } from "@/components/report/SummaryBar";
import { SectionCard } from "@/components/report/SectionCard";
import { PermitsSection } from "@/components/report/PermitsSection";
import { RecordsSection } from "@/components/report/RecordsSection";
import { ViolationsSection } from "@/components/report/ViolationsSection";
import { InspectionsSection } from "@/components/report/InspectionsSection";
import { OfficialSources } from "@/components/report/OfficialSources";
import { AskPropertyCheck } from "@/components/report/AskPropertyCheck";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { fmtFolio, fmtDate, transectFor } from "@/lib/permitcheck";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const findSource = (report, field) => (report.sources || []).find((s) => s.field === field) || null;

const FullAnalysisSection = ({ report }) => <ProfessionalAssistance report={report} />;


const Row = ({ icon: Icon, label, value, source, retrieved, lang, testid }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border/70 py-3 last:border-0">
    <div className="flex items-center gap-2.5 text-muted-foreground">
      <Icon className="h-4 w-4 shrink-0" />
      <span className="text-sm font-medium">{label}</span>
    </div>
    <div className="flex flex-col items-end gap-1.5 text-right">
      <span data-testid={testid} className="text-sm font-semibold text-foreground">{value}</span>
      {source && <SourceBadge {...source} retrievedAt={fmtDate(retrieved, lang)} />}
    </div>
  </div>
);

const ZoningSection = ({ report, lang }) => {
  const { t } = useLang();
  const zb = report.zoning_block || {};
  const zd = zb.detail || {};
  const zone = zb.designation;
  const info = transectFor(zone);
  const src = (zb.sources || [])[0];

  return (
    <div
      data-testid="section-zoning"
      className="relative overflow-hidden rounded-xl border border-primary/25 bg-primary p-6 text-primary-foreground"
    >
      <div className="absolute inset-0 pc-grid opacity-[0.08]" />
      <div className="relative">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-primary-foreground/60">
          {t("sec.zoning")} · Miami 21
        </p>

        {zone ? (
          <>
            <div className="flex items-center gap-3">
              <span data-testid="zoning-code-badge" className="rounded-lg bg-accent px-3 py-1.5 font-mono text-2xl font-bold text-accent-foreground">
                {zone}
              </span>
              {info && <span className="font-serif text-lg text-primary-foreground/90">{t(`t.${info.key}.label`)}</span>}
            </div>
            <p data-testid="transect-description" className="mt-3 text-sm leading-relaxed text-primary-foreground/80">
              {zb.description ? `${zb.description}. ` : ""}
              {info ? t(`t.${info.key}.scale`) : ""}
            </p>
          </>
        ) : zb.note ? (
          <p className="text-sm text-primary-foreground/80">{zb.note}</p>
        ) : (
          <p className="text-sm text-primary-foreground/70">{t("sum.na")}</p>
        )}

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {[
            [t("zon.futureLandUse"), zb.future_land_use],
            [t("zon.countyLandUse"), zb.county_land_use],
            [t("zon.responsible"), zb.municipality_responsible],
            [t("permit.type"), zd.Transect || null],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg bg-primary-foreground/10 px-3 py-2">
              <p className="font-mono text-[9px] uppercase tracking-wider text-primary-foreground/50">{k}</p>
              <p className="mt-0.5 text-sm font-semibold">{v || "—"}</p>
            </div>
          ))}
        </div>

        {src && (
          <div className="mt-4" data-testid="zoning-source-badge">
            <SourceBadge agency={src.agency} service={src.service} endpoint={src.endpoint} retrievedAt={fmtDate(report.retrieved_at, lang)} />
          </div>
        )}
      </div>
    </div>
  );
};

export const PropertyReport = ({ report }) => {
  const { t, lang } = useLang();
  const { user, openAuth } = useAuth();
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const enriched = { ...report, _retrieved: report.retrieved_at };

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const saveReport = async () => {
    if (!user) { openAuth(); return null; }
    try {
      const { data } = await axios.post(`${API}/saved-reports`, { report });
      setSaved(true);
      toast.success(t("report.saved"));
      window.dispatchEvent(new Event("pc-saved"));
      setTimeout(() => setSaved(false), 2000);
      return data.id;
    } catch {
      toast.error("Could not save report.");
      return null;
    }
  };

  const downloadPdf = () => {
    if (!user) { openAuth(); return; }
    downloadReportPdf(report, null, "Wise Property Search Report");
  };

  const shareReport = async () => {
    const id = await saveReport();
    if (!id) return;
    const link = `${window.location.origin}/?report=${id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("report.linkCopied"));
    } catch {
      toast.success(link);
    }
  };

  const copyFolio = async () => {
    try {
      await navigator.clipboard.writeText(report.folio);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };

  const item = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] } },
  };
  const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };

  return (
    <motion.div data-testid="property-report-container" variants={container} initial="hidden" animate="show" className="space-y-6">
      {/* Summary */}
      <motion.div variants={item}>
        <div className="mb-3 flex justify-end gap-2 pc-noprint">
          <Button onClick={saveReport} variant="outline" className="gap-2" data-testid="save-report-btn">
            {saved ? <Check className="h-4 w-4 text-emerald-500" /> : <Bookmark className="h-4 w-4" />}
            {saved ? t("report.saved") : t("report.save")}
          </Button>
          <Button onClick={shareReport} variant="outline" className="gap-2" data-testid="share-report-btn">
            <Share2 className="h-4 w-4" />
            {t("report.share")}
          </Button>
          <Button onClick={downloadPdf} variant="outline" className="gap-2" data-testid="download-pdf-btn">
            <Download className="h-4 w-4" />
            {t("report.download")}
          </Button>
        </div>
        <SummaryBar report={report} />
      </motion.div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4 flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-secondary/60 p-1 pc-noprint">
          <TabsTrigger value="overview" data-testid="tab-overview" className="whitespace-nowrap">{t("tab.overview")}</TabsTrigger>
          <TabsTrigger value="permits" data-testid="tab-permits" className="whitespace-nowrap">{t("tab.permits")}</TabsTrigger>
          <TabsTrigger value="violations" data-testid="tab-violations" className="whitespace-nowrap">{t("tab.violations")}</TabsTrigger>
          <TabsTrigger value="inspections" data-testid="tab-inspections" className="whitespace-nowrap">{t("tab.inspections")}</TabsTrigger>
          <TabsTrigger value="zoning" data-testid="tab-zoning" className="whitespace-nowrap">{t("tab.zoning")}</TabsTrigger>
          <TabsTrigger value="ask" data-testid="tab-ask" className="whitespace-nowrap gap-1"><Sparkles className="h-3.5 w-3.5" />{t("tab.ask")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 space-y-6 lg:col-span-7">
              <SectionCard icon={Landmark} title={t("sec.overview")} testid="section-overview">
                <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400"><ShieldCheck className="h-3 w-3" />{t("ask.verified")}</span>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">{report.site_address || report.normalized_address}</h2>
                    <p className="mt-1 text-sm text-muted-foreground">{[report.site_city, report.site_zip].filter(Boolean).join(", ")}</p>
                  </div>
                  <span data-testid="jurisdiction-tag" className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary">
                    <Landmark className="h-3.5 w-3.5" />{report.jurisdiction || "—"}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-secondary/50 p-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{t("report.folio")}</p>
                    <p data-testid="folio-number-badge" className="mt-0.5 font-mono text-lg font-bold text-foreground">{fmtFolio(report.folio)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button data-testid="copy-folio-button" onClick={copyFolio} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:border-primary/50">
                      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? t("report.copied") : t("report.copy")}
                    </button>
                    {findSource(report, "folio") && <SourceBadge {...findSource(report, "folio")} retrievedAt={fmtDate(report.retrieved_at, lang)} />}
                  </div>
                </div>
                <div className="mt-1">
                  <Row icon={MapPin} label={t("report.municipality")} value={report.municipality || report.jurisdiction || "—"} source={findSource(report, "municipality")} retrieved={report.retrieved_at} lang={lang} testid="municipality-field" />
                  <Row icon={FileText} label={t("rec.use")} value={report.property_use || "—"} source={findSource(report, "property_records")} retrieved={report.retrieved_at} lang={lang} testid="property-use-field" />
                  <Row icon={Layers} label={t("rec.landUse")} value={report.land_use || "—"} source={findSource(report, "land_use")} retrieved={report.retrieved_at} lang={lang} testid="land-use-field" />
                  <Row icon={User} label={t("rec.owner")} value={report.owner || "—"} source={findSource(report, "property_detail")} retrieved={report.retrieved_at} lang={lang} testid="owner-name-field" />
                </div>
              </SectionCard>
              <RecordsSection report={report} lang={lang} />
              <FullAnalysisSection report={report} />
            </div>
            <div className="col-span-12 space-y-6 lg:col-span-5">
              <ParcelMapPreview geometry={report.parcel_geometry} report={report} />
              <div className="pc-noprint"><DataSourceStatus /></div>
              <OfficialSources report={report} lang={lang} />
              <div className="pc-noprint"><ProvenanceInspector report={enriched} /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="permits" className="mt-0"><PermitsSection report={report} lang={lang} /></TabsContent>
        <TabsContent value="violations" className="mt-0"><ViolationsSection report={report} lang={lang} /></TabsContent>
        <TabsContent value="inspections" className="mt-0"><InspectionsSection report={report} lang={lang} /></TabsContent>
        <TabsContent value="zoning" className="mt-0"><ZoningSection report={report} lang={lang} /></TabsContent>
        <TabsContent value="ask" className="mt-0"><AskPropertyCheck report={report} /></TabsContent>
      </Tabs>
    </motion.div>
  );
};
